import {
  between, maxLength, minLength, integer, required, requiredIf,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { StringMap, TypeCampaignEnum } from '@/types';
import {
  checkIfTemplateOrRedirectContainsVoucher,
  getTemplateAndRedirect,
  reusePreviousCouponValues,
  voucherMethodsValues,
  voucherTypeValues,
  migrateOldVoucherConfig,
  buildTemplateVouchersRules,
  TemplateVouchersData,
  getDefaultEnabledOperator,
} from '@/composables/flows';
import { asInt } from '@/helpers';
import type { FlowOperatorMetadata, ValidationResult } from '@/types/flow-types';
import Form from './Form.vue';
import { canDeleteSendingAction } from '@/components/flows/operators/registry';

export interface SendRcsData {
  template_display_selector_id: number | null;
  reuse_a_previous_coupon: number;
  display_popup_on_site: number;
  template_display_reduction_method: number;
  template_display_reduction_descript: StringMap;
  template_display_reduction_type: number;
  template_display_reduction_identifiant: string;
  template_display_reduction_amount: number;
  template_display_reduction_duplicate: string;
  template_display_reduction_minorderamount: number;
  template_display_reduction_validtime: number;
  template_display_reduction_prefix: string;
  template_vouchers?: TemplateVouchersData;
  template_display_message_langs: string;
  template_message_expname: StringMap;
  /** sender_id of the chosen RCS agent (shared Shopimind or a launched dedicated agent). */
  rcs_sender_id: string;
  enabled_operator: number;
  [key: string]: any;
}

export function createDefaultData(data: Partial<SendRcsData> = {}, languages: string[] = [], campaignType?: string): SendRcsData {
  return {
    template_display_selector_id: data.template_display_selector_id != null ? asInt(data.template_display_selector_id) : null,
    reuse_a_previous_coupon: asInt(data.reuse_a_previous_coupon ?? 0),
    display_popup_on_site: asInt(data.display_popup_on_site ?? 0),
    template_display_reduction_method: asInt(data.template_display_reduction_method ?? 3),
    template_display_reduction_descript: data.template_display_reduction_descript ?? {},
    template_display_reduction_type: asInt(data.template_display_reduction_type ?? 1),
    template_display_reduction_identifiant: data.template_display_reduction_identifiant ?? '',
    template_display_reduction_amount: asInt(data.template_display_reduction_amount ?? 0),
    template_display_reduction_duplicate: data.template_display_reduction_duplicate ?? '',
    template_display_reduction_minorderamount: asInt(data.template_display_reduction_minorderamount ?? 0),
    template_display_reduction_validtime: asInt(data.template_display_reduction_validtime ?? 7),
    template_display_reduction_prefix: data.template_display_reduction_prefix ?? '',
    template_vouchers: data.template_vouchers && typeof data.template_vouchers === 'object' ? data.template_vouchers : {},
    template_display_message_langs: data.template_display_message_langs ?? JSON.stringify(languages),
    template_message_expname: data.template_message_expname ?? {},
    rcs_sender_id: data.rcs_sender_id ?? '',
    enabled_operator: asInt(data.enabled_operator ?? getDefaultEnabledOperator('boxsendrcs', campaignType)),
  };
}

interface ValidateContext {
  shopId: number;
}

const SendRcs: FlowOperatorMetadata<SendRcsData> = {
  id: 'boxsendrcs',
  kind: 'action',
  label: 'automatedScenarios.actions.sendRcs.label',
  icon: 'fa-comment-alt-dots',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
  canToggleEnabled: true,
  requiredServices: ['RcsServiceParameter'],

  menuActions: [
    {
      id: 'send-test-rcs',
      label: 'campaigns.common.operatorActions.sendTestMessage',
      icon: 'far fa-paper-plane',
      action: 'send-test-rcs',
    },
  ],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
        'boxsendrcs', 'boxdisplayfreepopup', 'boxdisplayfreemodel',
        'boxvisitpage',
      ],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
        'boxsendrcs', 'boxdisplayfreepopup', 'boxdisplayfreemodel',
        'boxvisitpage', 'boxchoosebulklist', 'boxaudiencesetup',
      ],
    },
  },

  Form,

  defaultData: (data?: Partial<SendRcsData>, campaignType?: string) => createDefaultData(data, [], campaignType),

  outputs: () => 1,

  async validate(data: SendRcsData, context?: ValidateContext): Promise<ValidationResult> {
    let mustConfigVoucher = false;
    let mustConfigMultiVoucher = false;
    const templatesWithVoucher: string[] = [];

    if (data.template_display_selector_id && data.template_display_selector_id > 0 && context?.shopId) {
      const idTemplate = asInt(data.template_display_selector_id);
      let templatesContainsVoucher = false;
      const listTemplate = await getTemplateAndRedirect(context.shopId, idTemplate);
      if (listTemplate !== null) {
        templatesContainsVoucher = checkIfTemplateOrRedirectContainsVoucher(listTemplate);

        listTemplate.forEach((template) => {
          if (typeof template.template_config === 'string') {
            const templateConfig = JSON.parse(template.template_config);
            if (templateConfig.hasVoucher) {
              templatesWithVoucher.push(String(template.id_template));
            }
            const tId = String(template.id_template);
            if (data.template_vouchers?.[tId]?.disabled && !templateConfig.hasConditionalVoucher) {
              data.template_vouchers[tId].disabled = false;
            }
          }
        });
      }
      if (templatesWithVoucher.length > 0) {
        mustConfigMultiVoucher = true;
      }
      if (templatesContainsVoucher && data.reuse_a_previous_coupon === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER) {
        mustConfigVoucher = true;
      }
    }

    migrateOldVoucherConfig(data, templatesWithVoucher);

    const rules: any = {
      template_display_reduction_method: {
        required: mustConfigVoucher,
      },
      template_display_reduction_descript: {
        required: false,
      },
      template_display_reduction_type: {
        required: mustConfigVoucher,
      },
      template_display_reduction_amount: {
        required: requiredIf(() => mustConfigVoucher && [voucherTypeValues.PERCENTAGE, voucherTypeValues.AMOUNT].includes(data.template_display_reduction_type)),
        integer,
      },
      template_display_reduction_minorderamount: {
        required: requiredIf(() => mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC),
        between: between(0, 10000000),
      },
      template_display_reduction_prefix: {
        minLength: minLength(2),
        maxLength: maxLength(32),
      },
      rcs_sender_id: {
        required,
      },
    };

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.MANUAL) {
      rules.template_display_reduction_identifiant = {
        required: true,
        minLength: minLength(2),
        maxLength: maxLength(45),
      };
    }

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC && data.template_display_reduction_type === voucherTypeValues.DUPLICATED) {
      rules.template_display_reduction_duplicate = {
        required: true,
        minLength: minLength(2),
        maxLength: maxLength(45),
      };
    }

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC) {
      rules.template_display_reduction_validtime = {
        required: true,
        between: between(1, 182),
      };
    }

    if (mustConfigMultiVoucher) {
      Object.assign(rules, buildTemplateVouchersRules(data.template_vouchers, templatesWithVoucher));
    }

    const v$ = useVuelidate(rules, data);
    const success = await v$.value.$validate();

    return {
      success,
      validate: v$,
    };
  },

  canBeDeleted(operatorId: string, flow: any): Promise<boolean | string> {
    return canDeleteSendingAction(flow, operatorId);
  },
};

export default SendRcs;
